import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, Fingerprint, Loader2, Lock, MailCheck } from 'lucide-react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useTenantBranding } from '../../hooks/useTenantBranding-runtime';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { patientHomePath } from '../../lib/clinicSite';
import { ClinicChoice, SignedIn, loginErrorMessage, patientLoginApi } from '../../lib/patientLoginApi';
import { track } from '../../lib/analytics';

/**
 * The top-level patient sign-in (/patient/login). Not tied to a clinic: the
 * patient proves who they are, then picks which of their clinics to open.
 * This is the link to give a patient who doesn't know their clinic's portal URL.
 *
 * One field takes an email or a mobile number and the action follows what's
 * typed: an email gets a sign-in link (the default — it reaches patients who
 * changed numbers and costs no SMS), a number gets a texted code. Password and
 * passkey are the alternatives for returning patients.
 */

type Stage = 'identify' | 'code' | 'link-sent' | 'password';

/** Looks like a phone number rather than an email (the server decides validity). */
const looksLikePhone = (value: string) => !value.includes('@') && value.replace(/\D/g, '').length >= 7;

const fieldClass =
  'block h-14 w-full rounded-2xl border border-white/70 bg-white/80 px-4 text-[16px] text-[#241a15] shadow-[0_1px_0_rgba(36,26,21,0.06)] ' +
  'placeholder:text-[#a8968a] transition focus:!border-[rgba(224,86,58,0.5)] focus:bg-white focus:outline-none focus:ring-4 focus:!ring-[rgba(224,86,58,0.15)]';
const primaryClass =
  'flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#241a15] px-5 text-[16px] font-semibold text-[#fff6ef] ' +
  'transition hover:bg-[#3a2b23] focus:outline-none focus-visible:ring-4 focus-visible:!ring-[rgba(224,86,58,0.3)] disabled:opacity-60';
const chipClass =
  'rounded-full border border-[rgba(36,26,21,0.1)] bg-white/50 px-4 py-2 text-[14px] font-medium text-[#241a15] backdrop-blur transition ' +
  'hover:bg-white/90 focus:outline-none focus-visible:ring-4 focus-visible:!ring-[rgba(224,86,58,0.25)]';
const quietLinkClass = 'inline-flex items-center gap-1.5 text-[14px] font-medium text-[#6b5a50] transition hover:text-[#241a15]';

export function PatientShell({ children }: { children: ReactNode }) {
  const { branding } = useTenantBranding();
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#fbf3ec] font-portal text-[#241a15] antialiased">
      {/* Soft light: two blurred washes over a warm-to-cool ground. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(165deg,#ffe9d9_0%,#fbf3ec_45%,#e2f1f3_100%)]" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-[rgba(255,180,140,0.35)] blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-24 h-[30rem] w-[30rem] rounded-full bg-[rgba(159,212,220,0.4)] blur-3xl" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-[440px] flex-col px-5 pb-8 pt-10 sm:pt-16">
        <div className="flex items-center gap-2.5">
          <span aria-hidden="true" className="h-6 w-6 rounded-full bg-[radial-gradient(circle_at_35%_35%,#ffa477,#e0563a)] shadow-[0_4px_12px_-4px_rgba(224,86,58,0.6)]" />
          <span className="font-portal-display text-[18px] font-semibold tracking-tight">{branding?.companyName || 'Patient portal'}</span>
        </div>
        <main className="mt-10 flex flex-1 flex-col motion-safe:animate-[portalIn_.35s_ease-out]">{children}</main>
        <p className="mt-10 flex items-center gap-2 text-[12.5px] text-[#8a776b]">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" /> Private and encrypted. Only you and your clinic see your records.
        </p>
      </div>
    </div>
  );
}

export function Headline({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div>
      <h1 className="font-portal-display text-[40px] font-bold leading-[1.02] tracking-[-0.025em] [text-wrap:balance] sm:text-[44px]">{children}</h1>
      {sub && <p className="mt-3 text-[16px] leading-relaxed text-[#6b5a50]">{sub}</p>}
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  if (!message) return null;
  return <p role="alert" className="rounded-2xl bg-[#fde4dc] px-4 py-3 text-[14px] text-[#8c2a14]">{message}</p>;
}

/** After any sign-in: enter the one clinic, or ask which. Shared with the magic-link landing. */
export function useEnterClinic() {
  const navigate = useNavigate();
  const setAuth = useDtpAuthStore(s => s.setAuth);
  return async (identityToken: string, clinic: ClinicChoice) => {
    const session = await patientLoginApi.enter(identityToken, clinic.physicianId);
    setAuth(session.token, session.patient, session.physicianId);
    navigate(patientHomePath(session.physicianId), { replace: true });
  };
}

// Tile tints cycle so neighbouring clinics never share a colour.
const TILE_TINTS = ['bg-[#cfe9ec]', 'bg-[#ffd9c4]', 'bg-[#e3ddf3]', 'bg-[#dcecd2]'];

export function ClinicPicker({ signedIn, onBack }: { signedIn: SignedIn; onBack?: () => void }) {
  const enterClinic = useEnterClinic();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  if (signedIn.clinics.length === 0) {
    return <div className="flex flex-col gap-6">
      <Headline sub="No clinic has a patient record with these details yet. Your clinic can add you, or send you a link to their store.">
        We couldn’t find your account
      </Headline>
      {onBack && <button onClick={onBack} className={`${quietLinkClass} self-start`}><ArrowLeft className="h-4 w-4" /> Try another way</button>}
    </div>;
  }

  const choose = async (clinic: ClinicChoice) => {
    setBusy(clinic.physicianId); setError('');
    try { await enterClinic(signedIn.identityToken, clinic); }
    catch (err) { setError(loginErrorMessage(err)); setBusy(null); }
  };

  return <div className="flex flex-col gap-7">
    <Headline sub={`You’re a patient at ${signedIn.clinics.length} clinics. Choose one to open.`}>
      Hi {signedIn.clinics[0].patientFirstName}. Where to?
    </Headline>
    <ul className="grid gap-3">
      {signedIn.clinics.map((clinic, i) => (
        <li key={clinic.physicianId}>
          <button onClick={() => choose(clinic)} disabled={busy !== null}
            className={`group relative flex w-full items-center justify-between overflow-hidden rounded-[22px] px-5 py-5 text-left transition ${TILE_TINTS[i % TILE_TINTS.length]} hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-18px_rgba(36,26,21,0.45)] focus:outline-none focus-visible:ring-4 focus-visible:!ring-[rgba(224,86,58,0.3)] disabled:opacity-70 motion-reduce:hover:translate-y-0`}>
            <span aria-hidden="true" className="pointer-events-none absolute -bottom-8 -right-6 h-24 w-24 rounded-full bg-white/40" />
            <span className="relative">
              <span className="block font-portal-display text-[21px] font-semibold tracking-tight">{clinic.clinicName}</span>
              <span className="block text-[13.5px] text-[#5a4a40]">Orders, results and account</span>
            </span>
            <span className="relative grid h-9 w-9 place-items-center rounded-full bg-white/70">
              {busy === clinic.physicianId ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />}
            </span>
          </button>
        </li>
      ))}
    </ul>
    <ErrorNote message={error} />
    {onBack && <button onClick={onBack} className={`${quietLinkClass} self-start`}><ArrowLeft className="h-4 w-4" /> Not you? Sign in differently</button>}
  </div>;
}

export function PatientLogin() {
  const enterClinic = useEnterClinic();
  const [stage, setStage] = useState<Stage>('identify');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const value = identifier.trim();
  const isPhone = looksLikePhone(value);
  useEffect(() => { track('login_viewed', { entry: 'login_page' }); }, []);

  const run = async (action: () => Promise<void>) => {
    setLoading(true); setError('');
    try { await action(); }
    catch (err) { setError(loginErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const finish = async (result: SignedIn) => {
    // One clinic: no picker, straight in.
    if (result.clinics.length === 1) return enterClinic(result.identityToken, result.clinics[0]);
    setSignedIn(result);
  };

  const backToStart = () => { setStage('identify'); setCode(''); setPassword(''); setError(''); };

  if (signedIn) {
    return <PatientShell><ClinicPicker signedIn={signedIn} onBack={() => { setSignedIn(null); backToStart(); }} /></PatientShell>;
  }

  const submitIdentify = (event: FormEvent) => {
    event.preventDefault();
    if (!value) return;
    // The method only: what was typed never leaves this page.
    track('login_method_selected', { method: isPhone ? 'code' : 'link' });
    if (isPhone) return run(async () => { await patientLoginApi.requestCode(value); setStage('code'); });
    return run(async () => { await patientLoginApi.requestLink(value); setStage('link-sent'); });
  };
  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    run(async () => finish(await patientLoginApi.verifyCode(value, code.trim())));
  };
  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    run(async () => finish(await patientLoginApi.password(value, password)));
  };

  const passkey = browserSupportsWebAuthn() && (stage === 'identify' || stage === 'password') && (
    <button onClick={() => { track('login_method_selected', { method: 'passkey' }); run(async () => finish(await patientLoginApi.passkey())); }} disabled={loading}
      className="mt-auto flex w-full items-center gap-4 rounded-[22px] sm:mt-2 border border-white/70 bg-white/60 p-4 text-left backdrop-blur transition hover:bg-white/80 focus:outline-none focus-visible:ring-4 focus-visible:!ring-[rgba(224,86,58,0.25)] disabled:opacity-60">
      <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl border-2 border-[rgba(224,86,58,0.8)] text-[#e0563a]"><Fingerprint className="h-6 w-6" aria-hidden="true" /></span>
      <span>
        <span className="block text-[15px] font-semibold">Sign in with a passkey</span>
        <span className="block text-[13px] text-[#6b5a50]">Face ID, Touch ID or your device’s screen lock</span>
      </span>
    </button>
  );

  return <PatientShell>
    <div className="flex flex-1 flex-col gap-7">
      {stage === 'identify' && <>
        <Headline sub="Orders, results and refills from every clinic you use.">Your care, in one place.</Headline>
        <form onSubmit={submitIdentify} className="flex flex-col gap-3">
          <label htmlFor="patient-identifier" className="sr-only">Email or mobile number</label>
          <input id="patient-identifier" className={fieldClass} value={identifier} onChange={e => setIdentifier(e.target.value)}
            placeholder="Email or mobile number" autoComplete="username" inputMode={isPhone ? 'tel' : 'email'} autoFocus required />
          <p className="min-h-[1.25rem] px-1 text-[13px] text-[#7a675b]" aria-live="polite">
            {value ? (isPhone ? 'We’ll text you a 6-digit code.' : 'We’ll email you a one-tap sign-in link.') : 'Use the email or number your clinic has on file.'}
          </p>
          <ErrorNote message={error} />
          <button type="submit" disabled={loading} className={primaryClass}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {isPhone ? 'Text me a code' : 'Email me a link'}
            {!loading && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          </button>
        </form>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={chipClass} onClick={() => { setStage('password'); setError(''); track('login_method_selected', { method: 'password' }); }}>Use a password</button>
        </div>
        {passkey}
      </>}

      {stage === 'code' && <>
        <Headline sub={<>If <strong className="font-semibold text-[#241a15]">{value}</strong> belongs to a patient account, we’ve texted a 6-digit code.</>}>Check your texts.</Headline>
        <form onSubmit={submitCode} className="flex flex-col gap-3">
          <label htmlFor="patient-code" className="text-[14px] font-medium">Verification code</label>
          <input id="patient-code" className={`${fieldClass} text-center font-portal-display text-[26px] tracking-[0.4em]`} value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="••••••" autoFocus required />
          <ErrorNote message={error} />
          <button type="submit" disabled={loading || code.length < 6} className={primaryClass}>
            {loading && <Loader2 className="h-5 w-5 animate-spin" />} Verify
          </button>
        </form>
        <button type="button" onClick={backToStart} className={`${quietLinkClass} self-start`}><ArrowLeft className="h-4 w-4" /> Use a different number</button>
      </>}

      {stage === 'link-sent' && <>
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white/70 text-[#e0563a]"><MailCheck className="h-7 w-7" aria-hidden="true" /></span>
        <Headline>Check your inbox.</Headline>
        <p role="status" className="-mt-3 text-[16px] leading-relaxed text-[#6b5a50]">
          If <strong className="font-semibold text-[#241a15]">{value}</strong> belongs to a patient account, we’ve emailed a sign-in link. It works once and expires in 15 minutes.
        </p>
        <button type="button" onClick={backToStart} className={`${quietLinkClass} self-start`}><ArrowLeft className="h-4 w-4" /> Use a different email</button>
      </>}

      {stage === 'password' && <>
        <Headline sub="Sign in with the mobile number and password you set in your account.">Welcome back.</Headline>
        <form onSubmit={submitPassword} className="flex flex-col gap-3">
          <label htmlFor="patient-phone" className="sr-only">Mobile number</label>
          <input id="patient-phone" className={fieldClass} value={identifier} onChange={e => setIdentifier(e.target.value)}
            placeholder="Mobile number" type="tel" autoComplete="tel" required />
          <label htmlFor="patient-password" className="sr-only">Password</label>
          <input id="patient-password" className={fieldClass} value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Password" type="password" autoComplete="current-password" required />
          <ErrorNote message={error} />
          <button type="submit" disabled={loading} className={primaryClass}>
            {loading && <Loader2 className="h-5 w-5 animate-spin" />} Sign in
          </button>
        </form>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={backToStart} className={`${quietLinkClass} self-start`}><ArrowLeft className="h-4 w-4" /> Get a link or code instead</button>
          <p className="px-1 text-[12.5px] text-[#8a776b]">No password yet? Sign in with a code, then add one from your account page.</p>
        </div>
        {passkey}
      </>}
    </div>
  </PatientShell>;
}
